import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Chip,
  Container,
  Avatar,
  Badge,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { useUserContext } from "../../providers/userProvider";
import { firestoreDB } from "../../firebase/firebase-config";
import { useNavigate } from "react-router-dom";
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
} from "firebase/auth";
import { uploadAvatar } from "../../services/firebaseService";
import { Edit, Visibility, VisibilityOff } from "@mui/icons-material";
import { useCustomThemeContext } from "../../providers/customThemeProvider";
import { Id } from "../../interfaces/types";
import { set } from "date-fns";

// Define the ProfilePage functional component
const ProfilePage: React.FC = () => {
  const { theme, updateTheme } = useCustomThemeContext();

  // Update the theme when the component mounts
  useEffect(() => {
    updateTheme();
  });

  const { user, userDetails, logout } = useUserContext();
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [bio, setBio] = useState<string>("");
  const [professions, setProfessions] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [eventRoles, setEventRoles] = useState<Map<Id, string[]>>(new Map());
  const [newProfession, setNewProfession] = useState<string>("");
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [avatar, setAvatar] = useState<File | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>("");
  const [showCurrentPassword, setShowCurrentPassword] =
    useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] =
    useState<boolean>(false);
  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(true);
  const navigate = useNavigate();

  // Fetch the user data asynchronously
  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        const userDoc = await getDoc(doc(firestoreDB, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setFirstName(userData.firstName || "");
          setLastName(userData.lastName || "");
          setUsername(userData.username || "");
          setBio(userData.bio || "");
          setProfessions(userData.professions || []);
          setRoles(userData.roles || []);
          setAvatarUrl(userData.profileImageUrl || "");
          setEventRoles(userData.eventRoles || new Map());
        }
      }
    };

    fetchUserData();
  }, [user]);

  //Update the user profile
  const handleUpdateProfile = async () => {
    try {
      let avatarUploadUrl: string = avatarUrl;
      if (avatar && userDetails) {
        avatarUploadUrl = (await uploadAvatar(avatar, userDetails)) || "";
      }
      // Check if there is a user
      if (user) {
        const userDoc = doc(firestoreDB, "users", user.uid);
        await updateDoc(userDoc, {
          firstName,
          lastName,
          username,
          bio,
          profileImageUrl: avatarUploadUrl,
          professions: professions.filter(
            (profession) => typeof profession === "string"
          ), // Ensure professions is a flat array of strings
          eventRoles: eventRoles,
        });
        setSnackbarMessage("Profile updated successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
        setTimeout(() => {
          setOpenSnackbar(false);
        }, 2000); // Delay navigation to allow Snackbar to be visible
      }
    } catch (error) {
      setSnackbarMessage("Error updating profile. Please try again.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setTimeout(() => {
        setOpenSnackbar(false);
      }, 2000);
    }
  };

  // Reauthenticate the user before deleting the profile
  const reauthenticateUser = async () => {
    if (user && user.email) {
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      try {
        await reauthenticateWithCredential(user, credential);
        console.log("Reauthentication successful");
      } catch (error) {
        console.error("Error reauthenticating user:", error);
        if ((error as any).code === "auth/wrong-password") {
          setSnackbarMessage("Current password is incorrect.");
        } else {
          setSnackbarMessage("Error reauthenticating user. Please try again.");
        }
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        throw error;
      }
    }
  };

  // const handleDeleteProfile = async () => {
  //   setOpenDialog(true);
  // };

  //Confirm the deletion of the profile
  const handleConfirmDelete = async () => {
    try {
      await reauthenticateUser();
      if (user) {
        // Delete user document from Firestore
        const userDoc = doc(firestoreDB, "users", user.uid);
        await deleteDoc(userDoc);

        // Delete user from Firebase Authentication
        await user.delete();

        // Log out the user
        await logout();

        setSnackbarMessage(
          "Profile deleted successfully! Redirecting to home page.."
        );
        setOpenSnackbar(true);
        setTimeout(() => {
          navigate("/");
        }, 2000); // Delay navigation to allow Snackbar to be visible
      }
    } catch (error) {
      console.error("Error deleting profile:", error);
      setSnackbarMessage("Error deleting profile. Please try again.");
      setOpenSnackbar(true);
    } finally {
      setOpenDialog(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  //Add a new profession
  const handleAddProfession = () => {
    if (newProfession && !professions.includes(newProfession)) {
      setProfessions([...professions, newProfession]);
      setNewProfession("");
    }
  };

  //Delete a profession
  const handleDeleteProfession = (professionToDelete: string) => {
    setProfessions(
      professions.filter((profession) => profession !== professionToDelete)
    );
  };

  // Change the user password
  const handleChangePassword = async () => {
    if (newPassword !== confirmNewPassword) {
      setPasswordsMatch(false);
      setSnackbarMessage("New passwords do not match.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    setPasswordsMatch(true);

    try {
      await reauthenticateUser();
      if (user) {
        await updatePassword(user, newPassword);
        setSnackbarMessage("Password updated successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword(""); // Clear password fields after changing
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmNewPassword(false);
      }
    } catch (error) {
      console.error("Error updating password:", error);
      if ((error as any).code === "auth/weak-password") {
        setSnackbarMessage("Password is too weak. Please try again.");
        setSnackbarSeverity("error");
      } else if ((error as any).code === "auth/invalid-credential") {
        setSnackbarMessage("Current password is incorrect.");
        setSnackbarSeverity("error");
      } else {
        setSnackbarMessage("Error updating password. Please try again.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    }
  };

  return (
    <>
      <Container disableGutters>
        <Box
          component="form"
          sx={{
            vh: "100%",
            mt: 3,
            ml: 1,
            width: "95%",
            justifySelf: "center",
            textAlign: "center",
          }}
        >
          <Typography variant="h4">Profile</Typography>
          {/* <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteProfile}
            >
              Delete Profile
            </Button>
          </Box> */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <label htmlFor="upload-avatar">
              <input
                id="upload-avatar"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    setAvatar(file);
                  }
                }}
              />
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={
                  <IconButton
                    component="span"
                    sx={{
                      cursor: "pointer",
                      zIndex: 1,
                      bgcolor: theme.palette.primary.main,
                      "&:hover": {
                        bgcolor: theme.palette.primary.main,
                        opacity: 0.7,
                      },
                    }}
                  >
                    <Edit />
                  </IconButton>
                }
              >
                <Avatar
                  alt={"Avatar of " + userDetails?.username}
                  src={avatar ? URL.createObjectURL(avatar) : avatarUrl}
                  sx={{ width: 150, height: 150, cursor: "pointer" }}
                />
              </Badge>
            </label>
          </Box>
          <Typography variant="h6" gutterBottom>
            Personal information
          </Typography>
          <TextField
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Preferred name"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
          />
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Professional information
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <TextField
              label="Add Profession"
              value={newProfession}
              onChange={(e) => setNewProfession(e.target.value)}
              fullWidth
              margin="normal"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddProfession}
              sx={{ ml: 2 }}
            >
              Add
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            {professions.map((profession) => (
              <Chip
                key={profession}
                label={profession}
                onDelete={() => handleDeleteProfession(profession)}
                sx={{
                  mr: 1,
                  mb: 1,
                  backgroundColor: "#757575",
                  color: "white",
                }} // Darker background color
              />
            ))}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Roles
            </Typography>
            {roles.map((role) => (
              <Chip
                key={role}
                label={role}
                sx={{
                  mr: 1,
                  mb: 1,
                  backgroundColor: "#757575",
                  color: "white",
                }} // Darker background color
              />
            ))}
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateProfile}
            fullWidth
            sx={{ mt: 2, mb: 2 }}
          >
            Update Profile
          </Button>
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>
            <TextField
              label="Current Password"
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="New Password"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm New Password"
              type={showConfirmNewPassword ? "text" : "password"}
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              fullWidth
              margin="normal"
              error={!passwordsMatch}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() =>
                        setShowConfirmNewPassword(!showConfirmNewPassword)
                      }
                    >
                      {showConfirmNewPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleChangePassword}
              fullWidth
              sx={{ mt: 2, mb: 2 }}
            >
              Change Password
            </Button>
          </Box>
        </Box>
      </Container>
      {/* 
      // Uncomment the following code to enable profile deletion confirmation dialog with cascading delete

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Reauthenticate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter your password to confirm profile deletion.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Confirm
          </Button>
        </DialogActions>
      </Dialog> */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProfilePage;
